/**
 * Vectara Chat API Integration
 * Implements the /v2/chats and /v2/chats/:chatId/turns endpoints following the official tutorial
 */

import { VECTARA_CONFIG, APP_CONFIG } from './config.js';

// Store the current chat ID for session management
let currentChatId = null;

export const getCurrentChatId = () => currentChatId;
export const setCurrentChatId = (chatId) => {
  currentChatId = chatId;
  console.log('Chat ID updated:', chatId);
};
export const resetChatSession = () => {
  currentChatId = null;
  console.log('Chat session reset.');
};

/**
 * Generate metadata filter based on query content
 */
function generateMetadataFilter(question) {
  const lowerQuestion = question.toLowerCase();
  if (lowerQuestion.includes('brave thinking') || lowerQuestion.includes('bti')) {
    return "doc.source = 'bti'";
  } else if (lowerQuestion.includes('coaching') || lowerQuestion.includes('transformation')) {
    return "doc.type = 'coaching'";
  } else if (lowerQuestion.includes('mary morrissey')) {
    return "doc.author = 'mary_morrissey'";
  }
  return '';
}

/**
 * Generate follow-up questions based on content analysis
 */
function generateFollowUpQuestions(answer, originalQuestion) {
  const lowerAnswer = answer.toLowerCase();
  const lowerQuestion = originalQuestion.toLowerCase();
  const followUpQuestions = [];

  // Topic-based follow-up questions
  if (lowerAnswer.includes('freedom') || lowerAnswer.includes('time') || lowerAnswer.includes('money')) {
    followUpQuestions.push(
      "What specific steps can I take today to create more time freedom in my life?",
      "How can I identify what's currently blocking my financial freedom?",
      "What would my ideal day look like with complete time and money freedom?"
    );
  } else if (lowerAnswer.includes('dream') || lowerAnswer.includes('vision') || lowerAnswer.includes('goal')) {
    followUpQuestions.push(
      "How do I turn my dreams into specific, actionable goals?",
      "What's the difference between a wish and a true dream?",
      "How can I stay motivated when my dreams feel overwhelming?"
    );
  } else if (lowerAnswer.includes('health') || lowerAnswer.includes('wellness') || lowerAnswer.includes('well-being')) {
    followUpQuestions.push(
      "What are the most important habits for maintaining optimal health?",
      "How does my physical health impact my ability to achieve my dreams?",
      "What daily practices can boost both my energy and mental clarity?"
    );
  } else if (lowerAnswer.includes('relationship') || lowerAnswer.includes('family') || lowerAnswer.includes('love')) {
    followUpQuestions.push(
      "How can I improve my most important relationships?",
      "What boundaries do I need to set to protect my energy for growth?",
      "How do I balance personal development with nurturing relationships?"
    );
  } else if (lowerAnswer.includes('career') || lowerAnswer.includes('work') || lowerAnswer.includes('vocation')) {
    followUpQuestions.push(
      "How do I discover my true calling or purpose?",
      "What steps can I take to align my career with my values?",
      "How can I create more fulfillment in my current work situation?"
    );
  } else if (lowerAnswer.includes('fear') || lowerAnswer.includes('belief') || lowerAnswer.includes('doubt')) {
    followUpQuestions.push(
      "What limiting beliefs are holding me back from my potential?",
      "How can I overcome fear when taking risks toward my dreams?",
      "What evidence do I have that contradicts my self-limiting thoughts?"
    );
  } else {
    // Generic transformation questions
    followUpQuestions.push(
      "What's the next small step I can take toward my biggest dream?",
      "How can I apply Brave Thinking principles to my current challenges?",
      "What would I attempt if I knew I couldn't fail?"
    );
  }

  // Return first 3 questions
  return followUpQuestions.slice(0, 3);
}

/**
 * Handle API errors with user-friendly messages
 */
function handleApiError(error) {
  console.error('Error querying Vectara:', error);
  if (error.message.includes('401')) {
    return '⚠️ Invalid API key. Please verify your Vectara API key and try again.';
  } else if (error.message.includes('429')) {
    return '⚠️ Rate limit exceeded. Please wait and try again later.';
  }
  return `⚠️ Error: ${error.message}`;
}

/**
 * Try different corpus key formats until one works
 */
async function tryCorpusKeyFormats(question, metadataFilter) {
  const url = 'https://api.vectara.io/v2/chats';

  const possibleCorpusKeys = [
    VECTARA_CONFIG.CORPUS_ID,
    `crp_${VECTARA_CONFIG.CORPUS_ID}`,
    `${VECTARA_CONFIG.CUSTOMER_ID}_${VECTARA_CONFIG.CORPUS_ID}`,
    parseInt(VECTARA_CONFIG.CORPUS_ID, 10),
    `corpus_${VECTARA_CONFIG.CORPUS_ID}`,
    `${VECTARA_CONFIG.CORPUS_ID}.0`,
    `c${VECTARA_CONFIG.CORPUS_ID}`
  ];

  console.log('Trying different corpus key formats:', possibleCorpusKeys);

  for (const corpusKey of possibleCorpusKeys) {
    console.log(`Attempting with corpus_key: ${corpusKey}`);

    const body = {
      query: question,
      search: {
        corpora: [
          {
            corpus_key: corpusKey,
            ...(metadataFilter ? { metadata_filter: metadataFilter } : {})
          }
        ],
        limit: 10
      },
      generation: {
        generation_preset_name: "vectara-summary-ext-24-05-sml",
        max_used_search_results: 5
      },
      stream_response: false,
      chat: {
        store: true
      }
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${VECTARA_CONFIG.API_KEY}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`Success with corpus_key: ${corpusKey}`);
        return data;
      }

      const errorText = await response.text();
      console.log(`Failed with corpus_key ${corpusKey}:`, errorText);

    } catch (error) {
      console.log(`Error with corpus_key ${corpusKey}:`, error);
    }
  }

  // Final fallback - try without corpus specification
  console.log('Trying without corpus specification...');
  try {
    const fallbackBody = {
      query: question,
      stream_response: false,
      chat: { store: true }
    };

    const fallbackResponse = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${VECTARA_CONFIG.API_KEY}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify(fallbackBody)
    });

    if (fallbackResponse.ok) {
      const fallbackData = await fallbackResponse.json();
      console.log('Success without corpus specification');
      return fallbackData;
    }

    const fallbackErrorText = await fallbackResponse.text();
    console.log('Failed without corpus specification:', fallbackErrorText);
  } catch (error) {
    console.log('Error without corpus specification:', error);
  }

  // Try v1 API as last resort
  console.log('Trying v1 API format...');
  try {
    const v1Url = 'https://api.vectara.io/v1/query';
    const v1Body = {
      query: [
        {
          query: question,
          queryContext: "",
          start: 0,
          numResults: 10,
          contextConfig: {
            charsBefore: 0,
            charsAfter: 0,
            sentencesBefore: 2,
            sentencesAfter: 2
          },
          corpusKey: [
            {
              customerId: parseInt(VECTARA_CONFIG.CUSTOMER_ID),
              corpusId: parseInt(VECTARA_CONFIG.CORPUS_ID),
              semantics: "DEFAULT"
            }
          ]
        }
      ]
    };

    const v1Response = await fetch(v1Url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${VECTARA_CONFIG.API_KEY}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify(v1Body)
    });

    if (v1Response.ok) {
      const v1Data = await v1Response.json();
      console.log('Success with v1 API');
      // Convert v1 response format to v2 format
      return {
        answer: v1Data.responseSet?.[0]?.summary?.[0]?.text || "No answer found in v1 response",
        chat_id: null // v1 doesn't have chat sessions
      };
    }

    const v1ErrorText = await v1Response.text();
    console.log('Failed with v1 API:', v1ErrorText);
  } catch (error) {
    console.log('Error with v1 API:', error);
  }

  throw new Error('No valid corpus key format found - tried v2 chat, v2 without corpus, and v1 query');
}

/**
 * Query Vectara Chat API - creates new chat session
 */
export async function queryVectara(question) {
  try {
    if (!VECTARA_CONFIG.API_KEY) {
      return "⚠️ No API key configured. Please check the setup.";
    }

    // Use v1 API directly since we know it works with this corpus
    console.log('Using Vectara v1 API directly...');
    const v1Url = 'https://api.vectara.io/v1/query';
    const v1Body = {
      query: [
        {
          query: question,
          queryContext: "",
          start: 0,
          numResults: 10,
          contextConfig: {
            charsBefore: 0,
            charsAfter: 0,
            sentencesBefore: 2,
            sentencesAfter: 2
          },
          corpusKey: [
            {
              customerId: parseInt(VECTARA_CONFIG.CUSTOMER_ID),
              corpusId: parseInt(VECTARA_CONFIG.CORPUS_ID),
              semantics: "DEFAULT"
            }
          ],
          summary: [
            {
              maxSummarizedResults: 10,
              responseLang: "eng",
              summarizerPromptName: "mockingbird-2.0"
            }
          ]
        }
      ]
    };

    const v1Response = await fetch(v1Url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${VECTARA_CONFIG.API_KEY}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify(v1Body)
    });

    if (!v1Response.ok) {
      const errorText = await v1Response.text();
      throw new Error(`API error: ${errorText}`);
    }

    const v1Data = await v1Response.json();
    console.log('v1 API Response:', v1Data);

    // Extract answer, sources, and follow-up questions from v1 response
    let answer = "No answer found.";
    let sources = [];
    let followUpQuestions = [];

    if (v1Data.responseSet && v1Data.responseSet[0]) {
      const responseSet = v1Data.responseSet[0];

      if (responseSet.summary && responseSet.summary[0] && responseSet.summary[0].text) {
        const fullText = responseSet.summary[0].text;

        // Check if follow-up questions are included in the response
        if (fullText.includes('---FOLLOWUP_QUESTIONS---')) {
          const parts = fullText.split('---FOLLOWUP_QUESTIONS---');
          answer = parts[0].trim();

          // Parse follow-up questions
          const questionsText = parts[1].trim();
          const questionLines = questionsText.split('\n').filter(line => line.trim());
          followUpQuestions = questionLines
            .filter(line => /^\d+\./.test(line.trim()))
            .map(line => line.replace(/^\d+\.\s*/, '').trim())
            .filter(q => q.length > 0)
            .slice(0, 3); // Limit to 3 questions
        } else {
          answer = fullText;
        }
      } else if (responseSet.response && responseSet.response.length > 0) {
        // Fallback to first search result if no summary
        answer = responseSet.response[0].text;
      }

      // Extract sources from response documents
      if (responseSet.response && responseSet.response.length > 0) {
        sources = responseSet.response.map((doc, index) => ({
          id: index + 1,
          title: 'Dream Builder Program',
          url: doc.metadata?.url || doc.metadata?.source_url || null,
          text: doc.text,
          score: doc.score || 0
        }));
      }
    }

    // Generate follow-up questions based on the content
    followUpQuestions = generateFollowUpQuestions(answer, question);

    return formatBTIResponse(answer, question, sources, followUpQuestions);
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * Create Chat Turn - continues existing chat session
 */
export async function createChatTurn(question) {
  const chatId = getCurrentChatId();

  if (!chatId) {
    return queryVectara(question);
  }

  try {
    const url = `https://api.vectara.io/v2/chats/${chatId}/turns`;
    const metadataFilter = generateMetadataFilter(question);

    const body = {
      query: question,
      search: {
        corpora: [
          {
            corpus_key: VECTARA_CONFIG.CORPUS_ID,
            ...(metadataFilter ? { metadata_filter: metadataFilter } : {}),
            lexical_interpolation: 0.01
          }
        ],
        limit: 10,
        context_configuration: {
          sentences_before: 2,
          sentences_after: 2
        }
      },
      generation: {
        max_used_search_results: 5,
        response_language: "eng"
      },
      stream_response: false
    };

    console.log('Creating chat turn:', body);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${VECTARA_CONFIG.API_KEY}`,
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      // Handle chat session expiry
      if (response.status === 404) {
        setCurrentChatId(null);
        return queryVectara(question);
      }

      throw new Error(`API error: ${await response.text()}`);
    }

    const data = await response.json();
    console.log('Chat turn response:', data);
    return formatBTIResponse(data.answer || "No answer found.", question, [], []);
  } catch (error) {
    return handleApiError(error);
  }
}

/**
 * Format response in BTI style with Mary Morrissey wisdom
 * @param {string} searchResult - Text from Vectara search result
 * @param {string} userMessage - Original user question
 * @param {Array} sources - Array of source documents
 * @param {Array} followUpQuestions - Array of follow-up questions
 * @returns {Object} - Structured response with text, sources, and follow-up questions
 */
function formatBTIResponse(searchResult, userMessage, sources = [], followUpQuestions = []) {
  // Return structured response with text, sources, and follow-up questions
  return {
    text: searchResult,
    sources: sources,
    followUpQuestions: followUpQuestions
  };
}

/**
 * Build prompt with conversation context and BTI system prompt
 * @param {string} userMessage - Current user message
 * @param {Array} conversationHistory - Previous messages
 * @returns {string} - Complete prompt with context
 */
function buildPromptWithContext(userMessage, conversationHistory) {
  let prompt = SYSTEM_PROMPT.content + "\n\n";

  // Add conversation context (last 5 messages)
  const recentHistory = conversationHistory.slice(-APP_CONFIG.MAX_CONTEXT_MESSAGES);
  if (recentHistory.length > 0) {
    prompt += "Recent conversation context:\n";
    recentHistory.forEach(msg => {
      const role = msg.sender === 'user' ? 'Human' : 'Assistant';
      prompt += `${role}: ${msg.content}\n`;
    });
    prompt += "\n";
  }

  prompt += `Please respond to this question in the style and wisdom of Mary Morrissey and BTI principles: ${userMessage}`;

  return prompt;
}

/**
 * Custom error class for Vectara API errors
 */
export class VectaraAPIError extends Error {
  constructor(message, status, details = null) {
    super(message);
    this.name = 'VectaraAPIError';
    this.status = status;
    this.details = details;
  }
}

/**
 * Retry wrapper with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} maxAttempts - Maximum retry attempts
 * @returns {Promise} - Result of successful function call
 */
export async function retryWithBackoff(fn, maxAttempts = APP_CONFIG.RETRY_ATTEMPTS) {
  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry on certain errors
      if (error instanceof VectaraAPIError) {
        if (error.status >= 400 && error.status < 500 && error.status !== 429) {
          throw error; // Client errors (except rate limiting)
        }
      }

      if (attempt === maxAttempts) {
        throw lastError;
      }

      // Calculate delay with exponential backoff and jitter
      const baseDelay = APP_CONFIG.RETRY_DELAY;
      const delay = baseDelay * Math.pow(2, attempt - 1) + Math.random() * 1000;

      console.log(`Attempt ${attempt} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}